import { get, writable } from 'svelte/store';
import {
  playerInstance,
  enemyInstance,
  dieHistory,
  actionHistory,
  techniqueHistory,
  lockedDie,
  activePlayer,
  inactivePlayer,
  options,
} from '../../store';
import {
  createDie,
  setInvokeTarget,
  type Die,
  type DieCreation,
  type DieRoller,
} from '../models/die.type';
import { DieFace } from '../models/face.type';
import {
  filterTechniques,
  fitPositions,
  getAvailableTechniques,
  isTechniquePossible,
  sortTechniques,
  validateTechnique,
  type Technique,
} from '../models/techniques.type';
import { clonePlayer, getModifiers, type Player } from '../models/player.type';
import type { Modifier } from '../models/modifier';
import { BattleState, type Battle } from '../models/battle.type';
import type { HistoryItem } from '../models/history.type';
import { calculateMazlovs, chooseTechnique } from '../utils/ai';
import { clone } from '../utils/utils';
import { DiceLedger, type LedgerSnapshot } from './ledger';
import { diceInSlots, ingredientDice, slotsEmpty, techniqueFull, type Slots } from './slots';

export type BattleData = {
  player: Player;
  enemy: Player;
  battle: Battle;
};

/** What the screen draws of the battle, handed over as a fresh object on every change. */
export type BattleView = {
  state: BattleState;
  /** Whether it is the player's turn rather than the enemy's. */
  playerActive: boolean;
  /** The dice still on the board, rolling. */
  board: Array<Die>;
  /** The dice locked in for this turn, in the tray. */
  locked: Array<Die>;
  /** The die in each slot of each technique on offer, by the technique's title. */
  slots: Record<string, Slots>;
  /** A copy of the active player's dice as they were dealt, for the die tips. */
  dealt: Array<Die>;
  rerollRemaining: number;
  /** Newest first. */
  history: Array<HistoryItem>;
};

/**
 * What the engine asks of whoever is showing the battle. All optional: with
 * none of them the battle still plays out, instantly and unseen, which is how
 * the tests run it.
 */
export type BattleHooks = {
  /**
   * Something to roll each die on the board, indexed by the die's `index`.
   * The board hands over its <Die> components, which animate the roll. Left
   * out, dice roll at once.
   */
  rollers?(): Array<DieRoller | null | undefined>;
  /** Lay out the board's dice again: fresh after a new turn, nudged after a roll. */
  scatter?(count: number, reset: boolean): void;
  /** A roll has begun with this many dice. */
  rolled?(count: number): void;
  stateChanged?(state: BattleState): void;
  techniqueUsed?(): void;
  ended?(outcome: BattleState.Win | BattleState.Lose): void;
};

/** How long the engine waits between beats: before the first roll, and between AI moves. */
const BEAT_MS = 1000;

/**
 * A player's dice, and any their traits and items add, made ready to roll.
 * `prefix` goes on each die's id, so two players' dice never share one.
 */
export function dealDice(player: Player, prefix: string): Array<Die> {
  let creations: Array<DieCreation> = clone(player.dice);
  getModifiers(player, 'getDice').forEach((mod) => {
    if (mod.getDice) {
      mod.getDice(creations).forEach((die) => {
        creations = creations.concat(die);
      });
    }
  });
  return creations.map((die: DieCreation, index: number) => {
    return createDie(die, index, prefix + index);
  });
}

/** Rolls a die on the spot, for when nothing is animating it. */
function instantRoller(die: Die): DieRoller {
  return {
    getDie: () => die,
    roll: async () => {
      die.roll();
      return die.value()[0] !== DieFace.Broken;
    },
  };
}

/**
 * One battle: whose turn it is, the dice rolled and locked, the techniques on
 * offer, the history and undo, and the AI's moves.
 *
 * The active player's dice are in `dice`, a ledger that gives each die one
 * place: the board, the tray, a technique's slots, or used. Everything else --
 * the board and tray lists, the slots, the dice the techniques on offer are
 * worked out from -- is read from it. `lockedDie` in the store is kept as a
 * copy of the tray, for the modals that read it.
 *
 * The players live in the `playerInstance` and `enemyInstance` history stores,
 * changed in place and then announced -- see CLAUDE.md on game state -- which
 * is why what the screen draws is published separately, through `view` and
 * `techniques`.
 */
export class BattleEngine {
  readonly data: BattleData;
  private hooks: BattleHooks;

  state: BattleState = BattleState.PreBattle;
  playerActive = true;
  /** Every die the active player has this turn, and where it is. */
  readonly dice = new DiceLedger();
  /** The active player's dice as dealt this turn, before any modifier had them. */
  dealt: Array<Die> = [];
  rerollMax = 0;
  rerollRemaining = 0;
  private rolling = false;

  /*
    A store rather than part of `view`. A technique's `possible` is set in
    place, so it is the same object before and after, and an {#each} in runes
    mode would not redraw it. An {#each} over a store keeps the legacy rule --
    same object, still checked.
  */
  readonly techniques = writable<Array<Technique>>([]);
  readonly view = writable<BattleView>(this.snapshot());

  private aiTimeout: ReturnType<typeof setTimeout> | undefined;
  private waitAI = false;
  private unsubscribers: Array<() => void> = [];
  /** Every timer still to fire, so destroy() can stop them. */
  private timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(data: BattleData, hooks: BattleHooks = {}) {
    this.data = data;
    this.hooks = hooks;

    const sync = () => this.syncActivePlayers();
    this.unsubscribers.push(
      playerInstance.subscribe(sync),
      enemyInstance.subscribe(sync),
      setInvokeTarget((die) => this.invoke(die)),
    );
    playerInstance.set(data.player);
    enemyInstance.set(data.enemy);

    techniqueHistory.length = 0;
  }

  /** Deals the first turn. */
  start() {
    this.startTurn();
    this.updatePlayers();
    this.updateDice();
    this.checkDied();
  }

  /** Lets go of the stores and stops anything still to come. */
  destroy() {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [];
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
  }

  /** setTimeout, but one destroy() can cancel. */
  private later(fn: () => void, ms: number) {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      fn();
    }, ms);
    this.timers.add(timer);
    return timer;
  }

  private cancel(timer: ReturnType<typeof setTimeout> | undefined) {
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(timer);
    }
  }

  private get active(): Player {
    return get(activePlayer);
  }
  private get inactive(): Player {
    return get(inactivePlayer);
  }

  /** The dice still on the board. */
  get board(): Array<Die> {
    return this.dice.in('board');
  }

  /** The dice locked in, in the tray. */
  get tray(): Array<Die> {
    return this.dice.in('tray');
  }

  /** The dice the techniques on offer are worked out from: the tray and the slots. */
  get available(): Array<Die> {
    return [...this.tray, ...this.dice.in('slot')];
  }

  /** The die in each of a technique's slots. */
  slots(technique: Technique): Slots {
    return this.dice.slotsOf(technique.title, technique.ingredients.length);
  }

  /** Whether every ingredient of a technique has its die. */
  full(technique: Technique) {
    return techniqueFull(technique, this.slots(technique));
  }

  /** A die invoked mid-battle -- by a potion, Infusion, a technique -- lands in the tray. */
  private invoke(die: Die) {
    // An index of its own: the ids of dice in play are unique, and so should
    // their indices be. It used to be the tray's length plus one, which a die
    // still in play could already have.
    const indices = [...this.dice.all(), ...this.dealt].map((d) => d.index);
    die.index = Math.max(-1, ...indices) + 1;
    this.dice.add(die, { in: 'tray' });
    this.updateDice();
  }

  /*
    Points activePlayer and inactivePlayer at whoever's turn it is.

    get() rather than a `$store` read in a component, because this runs inside
    the history stores' own subscriptions. In runes mode `$store` subscribes
    lazily, on first read, so a component's own subscription is queued behind
    these ones and still holds the old value while they run. After execute()
    put() a clone, the technique then acted on the snapshot just saved for
    undo -- and undo brought its effects back.
  */
  private syncActivePlayers() {
    const player = get(playerInstance);
    const enemy = get(enemyInstance);
    activePlayer.set(this.playerActive ? player : enemy);
    inactivePlayer.set(this.playerActive ? enemy : player);
  }

  private snapshot(): BattleView {
    return {
      state: this.state,
      playerActive: this.playerActive,
      board: this.board,
      locked: this.tray,
      slots: Object.fromEntries(get(this.techniques).map((t) => [t.title, this.slots(t)])),
      dealt: this.dealt,
      rerollRemaining: this.rerollRemaining,
      history: [...techniqueHistory],
    };
  }

  /** Publishes the dice, and everything else the screen draws, as it stands. */
  updateDice() {
    lockedDie.length = 0;
    lockedDie.push(...this.tray);
    this.view.set(this.snapshot());
  }

  /** Re-sets the player stores, announcing a change made to a player in place. */
  updatePlayers() {
    enemyInstance.set(get(enemyInstance));
    playerInstance.set(get(playerInstance));
  }

  lockDie(die: Die) {
    if (this.dice.placeOf(die)?.in == 'board') {
      this.dice.move(die, { in: 'tray' });
      this.updateDice();
    }
  }

  unlockDie(die: Die) {
    // A broken die stays where it is.
    if (die.value()[0] === DieFace.Broken) {
      return;
    }
    if (this.dice.placeOf(die)?.in == 'tray') {
      this.dice.move(die, { in: 'board' });
      this.updateDice();
    }
  }

  /** A die let go over the tray: locks it while rolling, takes it out of its technique after. */
  dropDieOnTray(die: Die) {
    switch (this.state) {
      case BattleState.Rolling:
        this.lockDie(die);
        break;

      case BattleState.ActionPhase:
        if (this.dice.placeOf(die)?.in == 'slot') {
          this.dice.move(die, { in: 'tray' });
        }
        this.updateDice();
        this.updateValidTechniques();
        break;
    }
    return true;
  }

  /** A die let go over a technique. False if it does not go there. */
  dropDieOnTechnique(die: Die, technique: Technique) {
    if (technique.possible === false) {
      return false;
    }
    const place = this.dice.placeOf(die);
    if (place?.in != 'tray' && place?.in != 'slot') {
      return false;
    }

    // Leaving the technique it is in, if any, is part of the move.
    const positions = fitPositions(die, technique, this.slots(technique));
    if (!positions) {
      console.warn('Die cannot be used in technique', die, technique);
      return false;
    }

    this.dice.move(die, { in: 'slot', technique: technique.title, slots: positions });
    this.updateDice();
    return true;
  }

  /** A die in the tray clicked: while rolling, it goes back on the board. */
  clickDie(die: Die) {
    if (this.state == BattleState.Rolling) {
      this.unlockDie(die);
    }
  }

  /** What rolls each die, by the die's `index`. */
  private rollersByIndex(): Array<DieRoller | null | undefined> {
    if (this.hooks.rollers) {
      return this.hooks.rollers();
    }
    const rollers: Array<DieRoller> = [];
    this.board.forEach((die) => (rollers[die.index] = instantRoller(die)));
    return rollers;
  }

  /**
   * What rolls each of `dice`, in the same order: the modifiers' roll hooks
   * reroll `dice[i]` with `rollers[i]`. Undefined for a die with nothing to
   * roll it, or whose roller is showing some other die.
   */
  private rollersFor(dice: Array<Die>, byIndex: Array<DieRoller | null | undefined>) {
    return dice.map((die) => {
      const roller = byIndex[die.index];
      return roller && roller.getDie() === die ? roller : undefined;
    });
  }

  async roll() {
    this.rolling = true;
    try {
      const byIndex = this.rollersByIndex();
      // The board changes as broken dice are locked, so the results are
      // matched to the dice as they stood when rolled.
      const dice = this.board;
      const rollers = this.rollersFor(dice, byIndex);
      const proms = rollers.map((roller) => (roller ? roller.roll() : Promise.resolve(true)));
      this.hooks.scatter?.(byIndex.length, false);
      this.hooks.rolled?.(rollers.filter((roller) => roller).length);

      getModifiers(this.active, 'rollStart').forEach((mod) => {
        if (mod.rollStart) {
          mod.rollStart(rollers, dice, this.rerollMax - this.rerollRemaining);
        }
      });

      const results = await Promise.all(proms);
      results.forEach((result, index) => {
        if (result === false) {
          this.lockDie(dice[index]);
        }
      });

      const proms2: Array<Promise<void | boolean>> = [];
      getModifiers(this.active, 'rollMid').forEach((mod) => {
        if (mod.rollMid) {
          const board = this.board;
          mod.rollMid(this.rollersFor(board, byIndex), board, proms2);
        }
      });
      await Promise.all(proms2);

      this.handleInstant(this.board);

      getModifiers(this.active, 'rollEnd').forEach((mod) => {
        if (mod.rollEnd) {
          const board = this.board;
          mod.rollEnd(this.rollersFor(board, byIndex), board);
        }
      });
    } finally {
      this.rolling = false;
    }

    this.updatePlayers();
    this.updateDice();
    this.checkDied();

    if (this.rerollRemaining <= 0) {
      this.lockin();
    }
  }

  reroll() {
    if (this.rerollRemaining <= 0 || this.rolling) {
      return;
    }
    this.rerollRemaining--;
    this.updateDice();

    return this.roll();
  }

  /** Locks every die left on the board and moves on to the action phase. */
  lockin() {
    [...this.board].reverse().forEach((die) => this.lockDie(die));
    this.setState(BattleState.ActionPhase);
    this.updateValidTechniques();
    this.updateDice();

    dieHistory.set(this.dice.snapshot());
    dieHistory.clear();
  }

  /** Fires any instant technique a rolled die makes on its own. */
  private handleInstant(dice: Array<Die>) {
    dice
      .filter((die) => die)
      .forEach(async (die) => {
        const techniques = filterTechniques([die]);
        getModifiers(this.active, 'getTechniques').forEach((mod) => {
          if (mod.getTechniques) {
            mod.getTechniques(techniques).map((technique) => {
              techniques.push(technique);
            });
          }
        });

        const technique = techniques
          .filter((technique) => technique.tags.includes('instant'))
          .find((technique) => technique.ingredients.toString() == die.value().toString());

        if (!technique) {
          return;
        }

        if (this.able(technique)) {
          const uses: Array<Modifier> = getModifiers(this.active, 'useTechnique');
          for (const use of uses) {
            if (use.useTechnique) {
              use.useTechnique(technique, this.inactive, this.active, [die]);
            }
          }

          await technique.activate(this.inactive, this.active, [die]);
        }

        this.addHistory(technique);

        this.updateDice();
        this.updatePlayers();
        this.checkDied();
      });
  }

  /** Whether the active player may use a technique, and the other player lets them. */
  private able(technique: Technique) {
    let able = true;
    getModifiers(this.active, 'canUseTechnique').forEach((mod) => {
      if (mod.canUseTechnique) {
        const result = mod.canUseTechnique(technique, this.active, this.inactive);
        if (result && result.able === false) {
          able = false;
        }
      }
    });
    getModifiers(this.inactive, 'canInterrupt').forEach((mod) => {
      if (mod.canInterrupt) {
        const result = mod.canInterrupt(technique, this.active, this.inactive);
        if (result && result.able === false) {
          able = false;
        }
      }
    });
    return able;
  }

  /** Fills a technique from the tray, as far as it goes. */
  selectIngredients(technique: Technique) {
    if (technique.possible === false) {
      return;
    }

    const dice = isTechniquePossible([], this.tray, technique);

    if (dice && dice.length) {
      // A die goes in the first slot it fits, so a wildcard -- which fits any
      // -- goes last, into whatever is left. First, it could take the slot
      // the one die that fitted there needed, and the recipe never filled.
      const wild = (die: Die) => die.value().includes(DieFace.Wildcard);
      dice.sort((a, b) => Number(wild(a)) - Number(wild(b)));
      dice.forEach((die) => {
        this.later(() => {
          this.dropDieOnTechnique(die, technique);
        }, 0);
      });
    }
  }

  async execute(technique: Technique) {
    // Only allow recipes that are valid. Checked before the players are put
    // in the history: putting them for a technique that then never fired left
    // the players' history a step ahead of the dice's, and undo out of step.
    if (!technique.possible) {
      return;
    }

    enemyInstance.put(clonePlayer(get(enemyInstance)));
    playerInstance.put(clonePlayer(get(playerInstance)));

    const slots = this.slots(technique);
    // One die per ingredient. activate() was handed the slots themselves --
    // an array of arrays -- and anything that looked at a die in it, as
    // Mana-Born and Anarchist do, threw.
    const dice = ingredientDice(slots);

    this.updateDice();

    if (this.able(technique)) {
      const uses = getModifiers(this.active, 'useTechnique');
      for (const use of uses) {
        if (use.useTechnique) {
          use.useTechnique(technique, this.inactive, this.active, dice);
        }
      }

      await technique.activate(this.inactive, this.active, dice);
    }

    diceInSlots(slots).forEach((die) => this.dice.move(die, { in: 'used' }));
    // Whatever else was sitting in a technique goes back to the tray.
    this.dice.empty({ in: 'tray' });

    dieHistory.put(this.dice.snapshot());
    this.updateDice();

    this.addHistory(technique);

    this.updatePlayers();
    this.checkDied();
    this.updateValidTechniques();

    this.hooks.techniqueUsed?.();
  }

  /** Activates a technique whose recipe is full. */
  clickTechnique(technique: Technique) {
    if (this.full(technique)) {
      this.execute(technique);
    }
  }

  checkDied() {
    getModifiers(get(enemyInstance), 'checkDied').forEach((mod) => {
      if (mod.checkDied) {
        mod.checkDied();
      }
    });

    getModifiers(get(playerInstance), 'checkDied').forEach((mod) => {
      if (mod.checkDied) {
        mod.checkDied();
      }
    });

    // The enemy first: when both fall at once, the player has won.
    if (get(enemyInstance).healthCurrent <= 0) {
      this.endGame(BattleState.Win);
    }
    if (get(playerInstance).healthCurrent <= 0 && !get(options).invincible) {
      this.endGame(BattleState.Lose);
    }
  }

  undo() {
    if (playerInstance.count() <= 0) {
      this.setState(BattleState.Rolling);
      // Back on the board with every die, not just the ones the clash locked:
      // the dice locked by hand during the roll were stranded in the tray.
      // unlockDie() still keeps a broken die where it is.
      this.dice.empty({ in: 'tray' });
      this.tray.forEach((die) => this.unlockDie(die));

      return;
    }
    playerInstance.undo();
    enemyInstance.undo();

    dieHistory.undo();
    // Every die back where it was, those that were sitting in a technique
    // included -- so there is nothing to send back, and nothing to copy.
    this.dice.restore(get(dieHistory) as LedgerSnapshot);
    techniqueHistory.shift();

    this.updateDice();
    this.updateValidTechniques();
  }

  private setState(newState: BattleState) {
    this.state = newState;
    this.hooks.stateChanged?.(newState);

    switch (this.state) {
      case BattleState.ActionPhase:
        // Infusion invokes a die here, which lands in the tray.
        getModifiers(this.active, 'actionPhaseStart').forEach((mod) => {
          if (mod.actionPhaseStart) {
            mod.actionPhaseStart();
          }
        });
        break;
    }
    this.updateDice();
  }

  endTurn() {
    getModifiers(this.active, 'endTurn').forEach((mod) => {
      if (mod.endTurn) {
        mod.endTurn();
      }
    });
    this.checkDied();

    this.playerActive = !this.playerActive;
    this.syncActivePlayers();

    this.dice.clear();
    this.updatePlayers();
    this.startTurn();
  }

  private startTurn() {
    this.setState(BattleState.Rolling);
    playerInstance.clear();
    enemyInstance.clear();
    dieHistory.clear();

    // The last turn's dice go, wherever they were -- a die left in a
    // technique included. It used to stay in the technique, whose object the
    // next player shared, and the enemy AI fired it with the player's die.
    this.dice.clear();
    let dice = dealDice(this.active, 'p');
    this.dealt = dealDice(this.active, 'p');
    this.dice.replaceBoard(dice);

    this.hooks.scatter?.(dice.length, true);

    this.rerollMax = this.active.rerollMax;

    getModifiers(this.active, 'beforeStartTurn').forEach((mod) => {
      if (mod.beforeStartTurn) {
        mod.beforeStartTurn();
      }
    });
    getModifiers(this.active, 'startTurn').forEach((mod) => {
      if (mod.startTurn) {
        const res = mod.startTurn({
          dice,
          rerollMax: this.rerollMax,
          activePlayer: this.active,
          inactivePlayer: this.inactive,
        });

        if (res) {
          if (res.dice) {
            dice = res.dice;
          }
          if (res.rerollMax) {
            this.rerollMax = res.rerollMax;
          }
        }
      }
    });
    // After every hook, as one may change the list in place rather than
    // hand back another.
    this.dice.replaceBoard(dice);

    this.rerollRemaining = this.rerollMax;

    this.addHistory({
      player: undefined,
      title: 'Change Turns',
      text: '',
      ingredients: ['endturn'],
    });

    this.later(() => {
      this.roll();

      if (!this.playerActive || get(options).autoPlay) {
        this.aiTimeout = this.later(() => this.AI(), BEAT_MS);
      }
    }, BEAT_MS);
  }

  private get over() {
    return this.state == BattleState.Win || this.state == BattleState.Lose;
  }

  private endGame(outcome: BattleState.Win | BattleState.Lose) {
    // Decided once. A roll that finishes someone checks for it twice -- once
    // itself and once as its instant technique resolves -- and each check
    // used to raise its own alert and move on to the battle-over screen.
    if (this.over) {
      return;
    }
    this.data.battle.outcome = outcome == BattleState.Win ? 'win' : 'lose';
    this.setState(outcome);
    this.hooks.ended?.(outcome);

    if (actionHistory.length) {
      this.addHistory({
        title: 'End Game',
        text: '',
        ingredients: ['grave'],
      });
    }
  }

  private addHistory(item: HistoryItem) {
    techniqueHistory.unshift({
      player: this.active,
      title: item.title,
      text: item.text,
      ingredients: item.ingredients,
      tags: item.tags,
      actions: [...actionHistory],
    });

    actionHistory.length = 0;

    this.updateDice();
  }

  /** Regroups the list on offer, when the option changes mid-battle. */
  regroup(grouped: boolean) {
    if (this.state == BattleState.ActionPhase) {
      this.techniques.set(sortTechniques([...get(this.techniques)], grouped));
    }
  }

  /** Works out the techniques the dice make, and publishes them. */
  updateValidTechniques() {
    const available = this.available;
    let techniques = getAvailableTechniques(available).map((technique) => {
      validateTechnique(technique, available);
      return technique;
    });

    getModifiers(this.active, 'getTechniques').forEach((mod) => {
      if (mod.getTechniques) {
        mod.getTechniques(techniques).map((technique) => {
          // Granted techniques miss the validation above. Unvalidated, they are
          // never `possible`, and execute() and the AI both pass them over.
          validateTechnique(technique, available);
          techniques.push(technique);
        });
      }
    });

    techniques = sortTechniques(techniques, get(options).groupTechniques);

    // A die in a technique no longer on offer goes back to the tray, rather
    // than staying somewhere nobody can see or reach.
    const offered = new Set(techniques.map((technique) => technique.title));
    const stranded = this.dice.techniquesHolding().filter((title) => !offered.has(title));
    if (stranded.length) {
      this.dice.empty({ in: 'tray' }, stranded);
    }

    this.techniques.set(techniques);
    this.updateDice();

    return techniques;
  }

  /** One move of the AI's turn, which then schedules the next. */
  private AI() {
    switch (this.state) {
      case BattleState.Rolling: {
        let locked = false;
        const target = this.rerollRemaining <= 1 ? 2 : 4;
        [...this.board].reverse().forEach((die) => {
          const face = die.value()[0];
          if (face !== DieFace.Blank && face !== DieFace.Broken && die.activeFace >= target) {
            this.lockDie(die);
            locked = true;
          }
        });
        if (!locked) {
          this.reroll();
        }
        this.waitAI = true;
        break;
      }

      case BattleState.ActionPhase: {
        if (this.waitAI) {
          this.waitAI = false;
          break;
        }
        const techniques = this.updateValidTechniques().filter((technique) => technique.possible);

        if (techniques.length) {
          const mazlovs = calculateMazlovs(this.active, this.inactive);
          const technique = chooseTechnique(techniques, mazlovs);

          // A technique with no dice in it yet is filled first.
          if (technique && slotsEmpty(this.slots(technique))) {
            this.selectIngredients(technique);
            this.aiTimeout = this.later(() => {
              this.clickTechnique(technique);
              this.aiTimeout = this.later(() => this.AI(), BEAT_MS);
            }, BEAT_MS);
            return;
          }

          if (!technique) {
            this.endTurn();
            return;
          }
          this.execute(technique);
        } else {
          this.endTurn();
          return;
        }
        break;
      }
    }

    this.cancel(this.aiTimeout);
    // Once the battle is decided the AI has nothing left to do. It used to
    // carry on ticking for good, long after the board was gone.
    if (!this.over) {
      this.aiTimeout = this.later(() => this.AI(), BEAT_MS);
    }
  }
}
